import { Component, computed, inject, signal } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { TranslocoService as TranslocoServiceNgneat } from '@ngneat/transloco';
import { CMSLanguageService, LocalesArray } from '@markant-platform/shared/data-access';
import { firstValueFrom, tap } from 'rxjs';
import { UpdateUserLanguageRequestDto, User, UserUpdateDTO } from '@markant-platform/shared/user/model';
import { AuthenticatedUserService, UserService } from '@markant-platform/shared/user/data-access';
import { toSignal } from '@angular/core/rxjs-interop';

@Component({
  selector: 'markant-platform-language-selection',
  templateUrl: './language-selection.component.html',
  styleUrls: ['language-selection.component.scss'],
})
export class LanguageSelectionComponent {
  showPopover = false;

  readonly cmsLocales = LocalesArray;

  private readonly authenticatedUserService: AuthenticatedUserService = inject(AuthenticatedUserService);
  private readonly translocoService: TranslocoService = inject(TranslocoService);
  private readonly translocoServiceNgneat: TranslocoServiceNgneat = inject(TranslocoServiceNgneat);
  private readonly cmsLanguageService: CMSLanguageService = inject(CMSLanguageService);
  private readonly userService: UserService = inject(UserService);

  loadingLocale = signal<string | undefined>(undefined);
  activeLocale = toSignal(this.translocoService.langChanges$.pipe(tap(lang => this.changeLanguage(lang))), {
    initialValue: this.translocoService.getActiveLang(),
  });

  localDisplayName = computed(() => this.getLocaleDisplayName(this.activeLocale()));

  togglePopover(): void {
    this.showPopover = !this.showPopover;
  }

  closePopover(): void {
    this.showPopover = false;
  }

  /**
   * Sets all language affected Service to the selected language
   *
   * @param localeShorthand - shorthand id of selected language
   */
  async handleLanguageSelect(localeShorthand: string): Promise<void> {
    this.loadingLocale.set(localeShorthand);
    await firstValueFrom(this.translocoService.load(localeShorthand));
    this.loadingLocale.set(undefined);
    this.translocoService.setActiveLang(localeShorthand);
    this.translocoServiceNgneat.setActiveLang(localeShorthand);
    setTimeout(() => this.closePopover(), 250);
    await this.updateUserLanguage(localeShorthand);
  }

  private changeLanguage(lang: string): void {
    localStorage.setItem('language', lang);
    document.documentElement.lang = lang;
    this.cmsLanguageService.activeCMSLanguage = lang;
  }

  private async updateUserLanguage(lang: string): Promise<void> {
    const user: User = await firstValueFrom(this.authenticatedUserService.getUser());
    const userId = user.id as string;
    const languageUpdate = {
      language: lang,
    } as UpdateUserLanguageRequestDto;

    await firstValueFrom(this.userService.patchUserLanguage(userId, languageUpdate));
    this.userService.updatedUser$.next({
      ...user,
      language: lang,
    } as UserUpdateDTO);
  }

  /**
   * Returns displayName of locale with matching provided shorthand string.
   *
   * @param selectedLocaleShorthand - shorthand for language to be displayed
   */
  getLocaleDisplayName(selectedLocaleShorthand?: string): string {
    if (selectedLocaleShorthand) {
      return this.cmsLocales.find(({ shorthand }) => shorthand === selectedLocaleShorthand)?.displayName ?? '';
    }
    return '';
  }
}
